<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="onClickBack" />
      </template>
      早期引出
    </Header>

    <div v-show="initialized" class="withdrawal px-3 py-4">
      <BForm @submit.stop.prevent>
        <div v-if="bankAccount" class="border border-outline-color radius-8">
          <div class="p-3">
            <div
              class="d-flex align-items-start pb-3 border-bottom border-outline-color"
            >
              <div class="left"><p class="text-size-14">振込先</p></div>
              <RouterLink
                :to="{ name: 'SetBank' }"
                class="right d-flex align-items-center"
              >
                <div class="flex-grow-1">
                  <p class="text-size-14">
                    {{ bankName }}
                  </p>
                  <p class="text-size-14">{{ bankAccount.branchName }}支店</p>
                  <p class="text-size-14">
                    普通&ensp;{{ bankAccount.accountNumber }}
                  </p>
                  <p class="text-size-14">{{ bankAccount.holderName }}</p>
                </div>
                <FeatherIcon
                  icon="chevron-right"
                  class="text-gray-05 flex-shrink-0 mt-1 ml-2"
                  size="1x"
                />
              </RouterLink>
            </div>
            <div class="d-flex align-items-start pt-3">
              <div class="left"><p class="text-size-14">残高</p></div>
              <div class="right">
                <p class="text-size-14">¥ {{ currency(balanceAmount) }}</p>
              </div>
            </div>
          </div>
          <TransferRequestCard
            :rewardTransactionHistories="rewardTransactionHistories"
            :balance="balanceAmount"
            @requestWithdraw="requestWithdraw"
          />
        </div>
      </BForm>
    </div>
  </div>
</template>

<script lang="ts">
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import Header from "@/components/organisms/common/Header.vue";
import TransferRequestCard from "@/components/organisms/mypage/TransferRequestCard.vue";
import { salaryService } from "@/app/services/SalaryService";
import { accountService } from "@/app/services/AccountService";
import { showErrorModal } from "@/utils/showErrorModal";
import { authStore } from "@/store/auth";
import { Component, Vue } from "vue-facing-decorator";
import { BankAccount } from "@/types/bank_account/BankAccount";
import { BalanceHistory } from "@/types/balance/BalanceHistory";
import { formatNumber } from "@/utils/number";
import { masterStore } from "@/store/master";

/**
 * Vee-validateで大小と数値の検証をしつつ、
 * フォームブラー時にコンマ付与の処理の共存が出来なさそうなため個別でバリデーションを実装
 */
@Component({
  components: {
    FeatherIcon,
    Header,
    TransferRequestCard
  }
})
export default class Withdrawal extends Vue {
  bankAccount: BankAccount | null = null;
  balanceAmount = 0;
  balanceUpdatedAt = "";
  applicationAmount = 0;
  fee = 550;
  isFormFocused = false;
  initialized = false;
  rewardTransactionHistories: BalanceHistory[] = [];

  get banks() {
    return masterStore.banks;
  }

  async created() {
    try {
      this.$loading.show();

      // アカウント情報を更新
      await accountService.updateUserData();
      this.bankAccount = await salaryService.fetchBankAccount();
      const balance = await salaryService.fetchBalance();
      this.balanceAmount = balance.amount;
      this.balanceUpdatedAt = balance.updatedAt;
      this.rewardTransactionHistories = (
        await salaryService.fetchWithdrawableRewardHistories({
          ordering: "created_at" // 古い順
        })
      ).results;

      this.initialized = true;
    } catch (error) {
      showErrorModal(this, error as Error);
    } finally {
      this.$loading.hide();
    }
  }

  onClickBack() {
    this.$router.back();
  }

  onClickFullAmount() {
    this.applicationAmount = this.balanceAmount;
  }

  onInputAmountField(value: string) {
    if (Number.isNaN(Number(value))) {
      this.applicationAmount = 0;
    } else {
      this.applicationAmount = Number(value);
    }
  }

  onFocusAmountField() {
    this.isFormFocused = true;
  }

  onBlurAmountField() {
    this.isFormFocused = false;
  }

  async requestWithdraw(
    transferAmount: number,
    rewardTransactionHistoryIds: number[]
  ) {
    try {
      await salaryService.requestWithdraw(
        transferAmount,
        rewardTransactionHistoryIds,
        this.balanceUpdatedAt
      );
      this.$router.push({ name: "WithdrawalFinished" });
    } catch (error) {
      await showErrorModal(this, error as Error);
      // 失敗時はリロードして再描画
      location.reload();
    } finally {
      this.$loading.hide();
    }
  }

  currency(value: number) {
    return formatNumber(value);
  }

  // フォームフォーカス時は、数字のまま表示し、ブラー時はコンマを付与した数値を表示
  get displayApplicationAmount() {
    return this.applicationAmount.toLocaleString();
  }

  get bankName() {
    if (!this.bankAccount || !this.bankAccount.bankCode) {
      return "";
    }
    const targetBank = this.banks.find(bank => {
      return bank.code === this.bankAccount?.bankCode;
    });
    return targetBank ? targetBank.name : "";
  }

  get transferAmount() {
    if (!this.applicationAmount) return 0;
    return this.applicationAmount - this.fee;
  }

  get error() {
    if (this.balanceAmount && this.applicationAmount > this.balanceAmount)
      return "残高が不足しています";
    return null;
  }

  get deposit() {
    return this.balanceAmount - (this.applicationAmount + this.fee);
  }
}
</script>

<style lang="scss" scoped>
.withdrawal {
  .d-flex {
    .left {
      flex: 0 0 100px;
      padding-right: 8px;
    }

    .right {
      flex: 1 1 auto;
    }
  }

  .border-input-form {
    position: relative;

    .before {
      position: absolute;
      top: 19px;
      left: 0;
      font-size: 24px;
    }

    .after {
      position: absolute;
      top: 22px;
      right: 0;
      width: 52px;
      height: 33px;
      padding: 0;
      border-radius: 4px;
    }
  }

  .disable-btn {
    padding: 5px 0;

    p {
      line-height: 24px;
    }

    .sub {
      line-height: 12px;
    }
  }
}

:deep(.app-textfield) {
  padding: 0 72px 8px 18px;
  font-size: 24px;
  font-weight: 600;
  border: none;
  border-bottom: 1px solid $primary-dark;
  border-radius: 0;
  outline: none;

  &:focus,
  &:active {
    border-color: $primary-dark;
    outline: none;
    box-shadow: none;
  }
}
</style>
